import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Ban, FileText, Plus } from 'lucide-react';

import { stationsApi } from '../../lib/stationsApi';
import { clientsAPI, trucksAPI } from '../../lib/api';
import { fetchAllPages } from '../../lib/fetchAllPages';
import { messageErreur } from '../../lib/erreurs';
import { libelle, nomLisible, optionsLibelles } from '../../lib/libelles';
import { arrondiDecimales, arrondiDevise, formatDate, formatHeure, formatMontant, formatNombre } from '../../lib/formats';
import { expectedCash, nozzleLiters } from '../../lib/stationCalc';
import { useAuth } from '../../context/AuthContext';
import { PageHeader } from '../../components/ui/page-header';
import { Aide } from '../../components/ui/aide';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { LoadingState } from '../../components/ui/loading-state';
import { EmptyState } from '../../components/ui/empty-state';
import { PhotoIndex } from '../../components/stations/PhotoIndex';
import { Readout } from '../../components/stations/Readout';
import { ShiftSteps } from '../../components/stations/ShiftSteps';
import { VarianceVerdict, depasseSeuil } from '../../components/stations/VarianceVerdict';
import { CELLULES, Champ, Panneau } from '../../components/stations/Panneau';
import { StationPicker, useDroitsStation, useStations } from '../../components/stations/StationPicker';

/**
 * Le poste du jour (lot 030), en quatre temps : ouvrir (index repris, fond de
 * caisse, pompistes ↔ pistolets) → pendant (ventes hors espèces, dépenses,
 * relevé au changement de prix) → clôturer (index de fin, caisse par moyen de
 * paiement, écarts) → arrêter la journée.
 *
 * Les ventes viennent des index : l'écran ne demande jamais un prix ni un
 * produit ; il compte les litres sur place (`stationCalc.js`) et laisse le
 * serveur valoriser les tranches de prix (aperçu de clôture). Sans « montants
 * visibles », la clôture se fait à l'aveugle : espèces comptées sans montant
 * attendu.
 */

export const NON_ESPECES = ['ORANGE_MONEY', 'MOOV_MONEY', 'WAVE', 'CARD', 'CREDIT', 'VOUCHER', 'FLEET', 'OWN_TRUCK'];
const AVEC_CLIENT = ['CREDIT', 'FLEET', 'VOUCHER'];
const LIBELLES_POSTE = ['Matin', 'Après-midi', 'Nuit'];
const BROUILLON = (shiftId) => `ecarbu.stations.cloture.${shiftId}`;

const nouvelleCle = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;

/** « 1 286 102,4 » → 1286102.4 ; vide → null. */
export const lireNombre = (texte) => {
  if (texte === null || texte === undefined) return null;
  const propre = String(texte).replace(/[\s  ]/g, '').replace(',', '.');
  if (propre === '') return null;
  const nombre = Number(propre);
  return Number.isFinite(nombre) ? nombre : null;
};

/** « Poste du matin », « Poste de l'après-midi », « Poste de nuit ». */
export const nomDuPoste = (label) => {
  const texte = String(label || '').trim();
  if (!texte) return 'Poste';
  const bas = texte.toLowerCase();
  if (bas === 'matin') return 'Poste du matin';
  if (bas === 'après-midi') return 'Poste de l’après-midi';
  if (bas === 'nuit') return 'Poste de nuit';
  return `Poste « ${texte} »`;
};

const signe = (valeur) => {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre)) return '—';
  const texte = formatNombre(Math.abs(nombre), 0);
  return nombre < 0 ? `−${texte}` : texte;
};

const masque = (visible, valeur, rendu) => (visible ? rendu(valeur) : 'Masqué');

/**
 * Aperçu local de la clôture : litres par pistolet (index de fin − dernier
 * relevé − essais), montant au prix en vigueur quand le pistolet n'a pas
 * changé de prix, espèces attendues et écarts. Le serveur reste juge des
 * tranches de prix (`close-preview`).
 */
export const apercuLocal = ({ shift, nozzles = [], prices = [], saisies = {}, especes, especesParPompiste = {} }) => {
  const prixParProduit = Object.fromEntries((prices || []).map((prix) => [prix.product_id, prix.unit_price]));
  const numeros = Object.fromEntries((nozzles || []).map((pistolet) => [pistolet.id, pistolet]));
  const affectations = Object.fromEntries((shift?.assignments || []).map((item) => [item.nozzle_id, item]));
  const releves = (shift?.readings || []).filter((releve) => !releve.voided_at && releve.kind !== 'CLOSING');
  const ouverture = releves.filter((releve) => releve.kind === 'OPENING');
  const ventesParPistolet = {};
  (shift?.sales || [])
    .filter((vente) => (vente.status || 'VALID') === 'VALID' && NON_ESPECES.includes(vente.payment_method))
    .forEach((vente) => {
      ventesParPistolet[vente.nozzle_id || ''] = (ventesParPistolet[vente.nozzle_id || ''] || 0) + Number(vente.total_amount || 0);
    });
  const lignes = ouverture
    .map((debut) => {
      const chaine = releves
        .filter((releve) => releve.nozzle_id === debut.nozzle_id)
        .sort((a, b) => (a.kind === 'OPENING' ? -1 : b.kind === 'OPENING' ? 1 : String(a.recorded_at).localeCompare(String(b.recorded_at))));
      const dernier = chaine[chaine.length - 1];
      let dejaReleves = 0;
      for (let i = 1; i < chaine.length; i += 1) {
        dejaReleves += nozzleLiters(chaine[i - 1].index, chaine[i].index, chaine[i].tests_liters || 0, chaine[i].meter_reset);
      }
      const saisie = saisies[debut.nozzle_id] || {};
      const fin = lireNombre(saisie.index);
      let litres = null;
      let erreur = null;
      if (fin !== null) {
        try {
          litres = arrondiDecimales(
            dejaReleves + nozzleLiters(dernier.index, fin, lireNombre(saisie.tests) || 0, Boolean(saisie.meterReset)),
            2,
          );
        } catch (probleme) {
          erreur = probleme.message;
        }
      }
      const pistolet = numeros[debut.nozzle_id] || {};
      const prix = prixParProduit[debut.product_id];
      const unPrix = chaine.length === 1;
      return {
        nozzle_id: debut.nozzle_id,
        number: pistolet.number ?? debut.nozzle_number,
        product_name: pistolet.product_name,
        attendant_id: affectations[debut.nozzle_id]?.attendant_id || null,
        attendant_name: affectations[debut.nozzle_id]?.attendant_name || null,
        start_index: debut.index,
        last_index: dernier.index,
        end_index: fin,
        liters: litres,
        unit_price: unPrix ? prix ?? null : null,
        amount: litres !== null && unPrix && prix !== undefined && prix !== null ? arrondiDevise(litres * Number(prix)) : null,
        error: erreur,
      };
    })
    .sort((a, b) => (a.number || 0) - (b.number || 0));
  const complet = lignes.length > 0 && lignes.every((ligne) => ligne.amount !== null);
  const ventes = complet ? lignes.reduce((somme, ligne) => somme + ligne.amount, 0) : null;
  const litres = lignes.every((ligne) => ligne.liters !== null)
    ? arrondiDecimales(lignes.reduce((somme, ligne) => somme + ligne.liters, 0), 2)
    : null;
  const nonEspeces = Object.values(ventesParPistolet).reduce((somme, valeur) => somme + valeur, 0);
  const depenses = Number(shift?.cash_expenses || 0);
  const fond = Number(shift?.opening_cash || 0);
  const attendues = ventes === null ? null : expectedCash(fond, ventes, nonEspeces, depenses);
  const comptees = lireNombre(especes);
  const ecart = attendues === null || comptees === null ? null : comptees - attendues;

  const seaux = {};
  lignes.forEach((ligne) => {
    const cle = ligne.attendant_id || '';
    seaux[cle] = seaux[cle] || { attendant_id: ligne.attendant_id, attendant_name: ligne.attendant_name, sales: 0, nonCash: 0 };
    seaux[cle].sales += ligne.amount || 0;
    seaux[cle].nonCash += ventesParPistolet[ligne.nozzle_id] || 0;
  });
  seaux[''] = seaux[''] || { attendant_id: null, attendant_name: null, sales: 0, nonCash: 0 };
  seaux[''].nonCash += ventesParPistolet[''] || 0;
  const declaresPompistes = Object.entries(especesParPompiste || {})
    .map(([id, valeur]) => [id, lireNombre(valeur)])
    .filter(([, valeur]) => valeur !== null);
  const totalPompistes = declaresPompistes.reduce((somme, [, valeur]) => somme + valeur, 0);
  const parId = Object.fromEntries(declaresPompistes);
  // La caisse du gérant n'a d'écart que si TOUS les pompistes ont déclaré leurs
  // espèces : sinon l'argent d'un pompiste serait compté chez le gérant.
  const affectes = Object.values(seaux).filter((seau) => seau.attendant_id).map((seau) => seau.attendant_id);
  const tousDeclares = affectes.length > 0 && affectes.every((id) => parId[id] !== undefined);
  const attendants = Object.values(seaux).map((seau) => {
    const attendu = seau.attendant_id
      ? arrondiDevise(seau.sales - seau.nonCash)
      : arrondiDevise(fond + seau.sales - seau.nonCash - depenses);
    let declare = null;
    if (seau.attendant_id) declare = parId[seau.attendant_id] ?? null;
    else if (tousDeclares && comptees !== null) declare = comptees - totalPompistes;
    return {
      attendant_id: seau.attendant_id,
      attendant_name: seau.attendant_name,
      expected_cash: complet ? attendu : null,
      declared_cash: declare,
      cash_variance: complet && declare !== null ? declare - attendu : null,
    };
  });
  return {
    lignes,
    sales_liters: litres,
    sales_amount: ventes,
    non_cash_total: nonEspeces,
    cash_expenses: depenses,
    opening_cash: fond,
    expected_cash: attendues,
    declared_cash: comptees,
    cash_variance: ecart,
    attendants,
  };
};

/** Corps de la clôture pour le serveur (`stationsApi.shifts.close`). */
export const corpsCloture = ({ saisies = {}, especes, declares = {}, especesParPompiste = {}, justification }) => ({
  closingReadings: Object.entries(saisies)
    .filter(([, saisie]) => lireNombre(saisie?.index) !== null)
    .map(([nozzleId, saisie]) => {
      const releve = { nozzle_id: nozzleId, index: lireNombre(saisie.index) };
      // Index lu sur la photo et confirmé tel quel : le relevé garde la photo et sa source.
      // Un index retouché à la main n'est plus celui de la photo.
      if (saisie.photoDocumentId && saisie.photoIndex !== undefined && lireNombre(saisie.index) === Number(saisie.photoIndex)) {
        releve.source = 'PHOTO';
        releve.photo_document_id = saisie.photoDocumentId;
      }
      const essais = lireNombre(saisie.tests);
      if (essais) releve.tests = essais;
      if (saisie.meterReset) {
        releve.meter_reset = true;
        releve.reason = String(saisie.motif || '').trim();
      }
      return releve;
    }),
  declaredPayments: {
    ...Object.fromEntries(
      Object.entries(declares || {})
        .map(([code, valeur]) => [code, lireNombre(valeur)])
        .filter(([, valeur]) => valeur !== null),
    ),
    ...(lireNombre(especes) !== null ? { CASH: lireNombre(especes) } : {}),
  },
  declaredCashByAttendant: Object.fromEntries(
    Object.entries(especesParPompiste || {})
      .map(([id, valeur]) => [id, lireNombre(valeur)])
      .filter(([, valeur]) => valeur !== null),
  ),
  justification: String(justification || '').trim() || undefined,
});

const lireBrouillon = (shiftId) => {
  try {
    return JSON.parse(window.localStorage.getItem(BROUILLON(shiftId)) || 'null');
  } catch {
    return null;
  }
};

const ecrireBrouillon = (shiftId, valeur) => {
  try {
    if (valeur) window.localStorage.setItem(BROUILLON(shiftId), JSON.stringify(valeur));
    else window.localStorage.removeItem(BROUILLON(shiftId));
    return true;
  } catch {
    return false;
  }
};

// --- 1. Ouverture ---------------------------------------------------------------------------

const OuvertureEtape = ({ contexte, stationId, peutEcrire, onOpened }) => {
  const [label, setLabel] = useState(contexte.default_label || 'Matin');
  const [fond, setFond] = useState('');
  const [affectations, setAffectations] = useState({});
  const [modifs, setModifs] = useState({});
  const [enCours, setEnCours] = useState(false);
  const cle = useRef(nouvelleCle());
  const sansPrix = (contexte.prices || []).filter((prix) => prix.unit_price === null || prix.unit_price === undefined);
  const optionsPompistes = useMemo(
    () => [{ value: '', label: 'Sans pompiste' }, ...(contexte.attendants || []).map((item) => ({ value: item.id, label: item.name }))],
    [contexte.attendants],
  );
  const optionsLibelle = useMemo(() => LIBELLES_POSTE.map((texte) => ({ value: texte, label: texte })), []);

  const ouvrir = async (event) => {
    event.preventDefault();
    setEnCours(true);
    try {
      const ouvert = await stationsApi.shifts.open(stationId, {
        id: cle.current,
        label,
        openingCash: lireNombre(fond) || 0,
        assignments: Object.entries(affectations)
          .filter(([, attendant]) => attendant)
          .map(([nozzleId, attendant]) => ({ nozzle_id: nozzleId, attendant_id: attendant })),
        readingsOverride: Object.entries(modifs)
          .filter(([, modif]) => modif?.actif && lireNombre(modif.index) !== null)
          .map(([nozzleId, modif]) => ({ nozzle_id: nozzleId, index: lireNombre(modif.index), reason: String(modif.motif || '').trim() })),
      });
      toast.success(`${nomDuPoste(ouvert?.label)} ouvert`);
      cle.current = nouvelleCle();
      onOpened(ouvert);
    } catch (probleme) {
      toast.error(messageErreur(probleme, 'Impossible d’ouvrir le poste'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <form onSubmit={ouvrir} className="grid gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
      <Panneau
        titre="Index des pistolets"
        droite={<span className="text-xs text-muted-foreground">Repris de la dernière clôture</span>}
      >
        <table className={CELLULES}>
          <thead>
            <tr>
              <th className="text-left">Pistolet</th>
              <th className="text-left">Produit</th>
              <th className="text-left">Pompiste</th>
              <th className="text-right">Index de début</th>
              <th aria-label="Modifier" />
            </tr>
          </thead>
          <tbody>
            {(contexte.nozzles || []).map((pistolet) => {
              const modif = modifs[pistolet.id] || {};
              return (
                <tr key={pistolet.id}>
                  <td className="whitespace-nowrap font-medium">Pistolet {pistolet.number}</td>
                  <td>{pistolet.product_name || libelle('fuelProduct', pistolet.product_code, 'Produit à préciser')}</td>
                  <td className="min-w-[10rem]">
                    <SearchableSelect
                      value={affectations[pistolet.id] || ''}
                      onValueChange={(valeur) => setAffectations((avant) => ({ ...avant, [pistolet.id]: valeur }))}
                      options={optionsPompistes}
                      aria-label={`Pompiste du pistolet ${pistolet.number}`}
                      placeholder="Choisir"
                      className="h-8"
                    />
                  </td>
                  <td className="text-right">
                    {modif.actif ? (
                      <div className="grid justify-items-end gap-1">
                        <Input
                          aria-label={`Index de début du pistolet ${pistolet.number}`}
                          inputMode="decimal"
                          value={modif.index ?? ''}
                          onChange={(event) => setModifs((avant) => ({ ...avant, [pistolet.id]: { ...modif, index: event.target.value } }))}
                          className="h-8 w-36 text-right tabular-nums"
                        />
                        <Input
                          aria-label={`Motif de la modification du pistolet ${pistolet.number}`}
                          placeholder="Motif"
                          value={modif.motif ?? ''}
                          onChange={(event) => setModifs((avant) => ({ ...avant, [pistolet.id]: { ...modif, motif: event.target.value } }))}
                          className="h-8 w-56"
                          required
                        />
                      </div>
                    ) : (
                      <Readout value={pistolet.current_index} label={`Index du pistolet ${pistolet.number}`} />
                    )}
                  </td>
                  <td className="text-right">
                    {peutEcrire && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setModifs((avant) => ({
                            ...avant,
                            [pistolet.id]: modif.actif ? { actif: false } : { actif: true, index: String(pistolet.current_index ?? ''), motif: '' },
                          }))
                        }
                      >
                        {modif.actif ? 'Reprendre' : 'Modifier'}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Panneau>

      <div className="grid content-start gap-4">
        <Panneau titre="Ouverture">
          <div className="grid gap-3 px-3 sm:grid-cols-2">
            <Champ id="poste-libelle" label="Poste">
              <SearchableSelect id="poste-libelle" value={label} onValueChange={setLabel} options={optionsLibelle} aria-label="Poste" />
            </Champ>
            <Champ id="poste-fond" label="Fond de caisse (FCFA)">
              <Input
                id="poste-fond"
                inputMode="numeric"
                value={fond}
                onChange={(event) => setFond(event.target.value)}
                placeholder="0"
                className="text-right tabular-nums"
              />
            </Champ>
          </div>
        </Panneau>
        <Panneau titre="Prix en vigueur" droite={<span className="text-xs text-muted-foreground">FCFA le litre</span>}>
          <table className={CELLULES}>
            <tbody>
              {(contexte.prices || []).map((prix) => (
                <tr key={prix.product_id}>
                  <td>{prix.product_name}</td>
                  <td className="text-right tabular-nums">
                    {prix.unit_price === null || prix.unit_price === undefined ? (
                      <Badge variant="destructive">Sans prix</Badge>
                    ) : (
                      formatNombre(prix.unit_price, 0)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sansPrix.length > 0 && (
            <p className="px-3 pt-2 text-sm">
              <Link to="/stations/prix" className="font-medium text-primary underline-offset-2 hover:underline">
                Saisir le prix
              </Link>
            </p>
          )}
        </Panneau>
        {peutEcrire && (
          <div className="flex justify-end">
            <Button type="submit" disabled={enCours || sansPrix.length > 0 || !(contexte.nozzles || []).length}>
              Ouvrir le poste
            </Button>
          </div>
        )}
      </div>
    </form>
  );
};

// --- 2. Pendant le poste ---------------------------------------------------------------------

const VenteDialog = ({ open, onOpenChange, stationId, contexte, onSaved }) => {
  const [form, setForm] = useState({});
  const [clients, setClients] = useState([]);
  const [camions, setCamions] = useState([]);
  const [vehicules, setVehicules] = useState([]);
  const [bon, setBon] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const cle = useRef(nouvelleCle());
  useEffect(() => {
    if (!open) return;
    setForm({ nozzle_id: '', payment_method: 'ORANGE_MONEY', quantity_liters: '', amount: '' });
    setBon(null);
    cle.current = nouvelleCle();
  }, [open]);
  const methode = form.payment_method;
  const clientChoisi = form.client_id;
  // Véhicules déclarés du client (vente de flotte) : liste complète, choix par recherche.
  useEffect(() => {
    setVehicules([]);
    if (!open || methode !== 'FLEET' || !clientChoisi) return;
    let annule = false;
    stationsApi.credit.vehicles
      .list(clientChoisi)
      .then((liste) => !annule && setVehicules(liste || []))
      .catch((probleme) => toast.error(messageErreur(probleme, 'Impossible de charger les véhicules')));
    return () => {
      annule = true;
    };
  }, [open, methode, clientChoisi]);
  // Bon carburant scanné ou saisi : son état est vérifié avant l'enregistrement.
  const verifierBon = async () => {
    const code = (form.voucher_code || '').trim();
    if (!code) return;
    try {
      setBon(await stationsApi.credit.vouchers.lookup(code, clientChoisi));
    } catch (probleme) {
      setBon(null);
      toast.error(messageErreur(probleme, 'Ce bon est inconnu'));
    }
  };
  useEffect(() => {
    if (!open || !AVEC_CLIENT.includes(methode) || clients.length) return;
    fetchAllPages((params) => clientsAPI.getAllPaged(params))
      .then((resultat) => setClients(resultat.items || []))
      .catch((probleme) => toast.error(messageErreur(probleme, 'Impossible de charger les clients')));
  }, [open, methode, clients.length]);
  useEffect(() => {
    if (!open || methode !== 'OWN_TRUCK' || camions.length) return;
    fetchAllPages((params) => trucksAPI.getAllPaged(params))
      .then((resultat) => setCamions(resultat.items || []))
      .catch((probleme) => toast.error(messageErreur(probleme, 'Impossible de charger les camions')));
  }, [open, methode, camions.length]);

  const pistolets = useMemo(() => {
    const dansLePoste = new Set((contexte.shift?.readings || []).filter((r) => r.kind === 'OPENING').map((r) => r.nozzle_id));
    return (contexte.nozzles || [])
      .filter((pistolet) => dansLePoste.has(pistolet.id))
      .map((pistolet) => ({ value: pistolet.id, label: `Pistolet ${pistolet.number}`, description: pistolet.product_name }));
  }, [contexte]);
  const pistolet = (contexte.nozzles || []).find((item) => item.id === form.nozzle_id);
  const prix = (contexte.prices || []).find((item) => item.product_id === pistolet?.product_id)?.unit_price;
  const litres = lireNombre(form.quantity_liters);
  const montant = lireNombre(form.amount);
  const estime = litres !== null && prix ? arrondiDevise(litres * Number(prix)) : null;
  const changer = (cleChamp) => (valeur) =>
    setForm((avant) => ({ ...avant, [cleChamp]: valeur?.target ? valeur.target.value : valeur }));

  const enregistrer = async (event) => {
    event.preventDefault();
    setEnCours(true);
    try {
      await stationsApi.sales.record(stationId, {
        id: cle.current,
        nozzle_id: form.nozzle_id,
        payment_method: methode,
        quantity_liters: litres ?? undefined,
        amount: litres === null ? montant ?? undefined : undefined,
        client_id: AVEC_CLIENT.includes(methode) ? form.client_id || undefined : undefined,
        voucher_code: methode === 'VOUCHER' ? (form.voucher_code || '').trim() || undefined : undefined,
        vehicle_plate: methode === 'FLEET' ? form.vehicle_plate || undefined : undefined,
        truck_id: methode === 'OWN_TRUCK' ? form.truck_id || undefined : undefined,
        customer_name: form.customer_name || undefined,
        notes: form.notes || undefined,
      });
      toast.success('Vente enregistrée');
      onSaved();
      onOpenChange(false);
    } catch (probleme) {
      toast.error(messageErreur(probleme, 'Impossible d’enregistrer la vente'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1">
            Vente hors espèces
            <Aide titre="Vente hors espèces">
              Crédit client, bon carburant, flotte, mobile money, carte ou camion de la société. Les ventes payées en
              espèces ne se saisissent pas : les index des pistolets les comptent. Le prix est celui en vigueur.
            </Aide>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={enregistrer} className="grid gap-4 sm:grid-cols-2">
          <Champ id="vente-pistolet" label="Pistolet">
            <SearchableSelect id="vente-pistolet" value={form.nozzle_id || ''} onValueChange={changer('nozzle_id')} options={pistolets} aria-label="Pistolet" placeholder="Choisir" />
          </Champ>
          <Champ id="vente-moyen" label="Paiement">
            <SearchableSelect
              id="vente-moyen"
              value={methode || ''}
              onValueChange={changer('payment_method')}
              options={optionsLibelles('station_paiement', NON_ESPECES)}
              aria-label="Paiement"
            />
          </Champ>
          <Champ id="vente-litres" label="Litres">
            <Input
              id="vente-litres"
              inputMode="decimal"
              value={form.quantity_liters || ''}
              onChange={(event) => setForm((avant) => ({ ...avant, quantity_liters: event.target.value, amount: '' }))}
              className="text-right tabular-nums"
            />
          </Champ>
          <Champ id="vente-montant" label="ou montant (FCFA)">
            <Input
              id="vente-montant"
              inputMode="numeric"
              value={form.amount || ''}
              onChange={(event) => setForm((avant) => ({ ...avant, amount: event.target.value, quantity_liters: '' }))}
              className="text-right tabular-nums"
            />
          </Champ>
          {AVEC_CLIENT.includes(methode) && (
            <div className="sm:col-span-2">
              <Champ id="vente-client" label="Client">
                <SearchableSelect
                  id="vente-client"
                  value={form.client_id || ''}
                  onValueChange={changer('client_id')}
                  options={clients.map((client) => ({ value: client.id, label: nomLisible(client.name, client.code), keywords: [client.phone].filter(Boolean) }))}
                  aria-label="Client"
                  placeholder="Choisir le client"
                  searchPlaceholder="Rechercher un client…"
                />
              </Champ>
            </div>
          )}
          {methode === 'FLEET' && (
            <Champ id="vente-plaque" label="Véhicule">
              {vehicules.length > 0 ? (
                <SearchableSelect
                  id="vente-plaque"
                  value={form.vehicle_plate || ''}
                  onValueChange={changer('vehicle_plate')}
                  options={vehicules.map((vehicule) => ({ value: vehicule.plate, label: vehicule.plate, description: vehicule.label || undefined }))}
                  aria-label="Véhicule"
                  placeholder="Choisir le véhicule"
                  searchPlaceholder="Rechercher une plaque…"
                />
              ) : (
                <Input id="vente-plaque" value={form.vehicle_plate || ''} onChange={changer('vehicle_plate')} placeholder="Immatriculation" />
              )}
            </Champ>
          )}
          {methode === 'VOUCHER' && (
            <div className="sm:col-span-2">
              <Champ id="vente-bon" label="Bon carburant (code scanné ou numéro)">
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    id="vente-bon"
                    value={form.voucher_code || ''}
                    onChange={(event) => {
                      setBon(null);
                      changer('voucher_code')(event);
                    }}
                    onBlur={verifierBon}
                    maxLength={100}
                    className="min-w-0 flex-1"
                  />
                  <Button type="button" variant="outline" onClick={verifierBon} disabled={!(form.voucher_code || '').trim()}>
                    Vérifier
                  </Button>
                  {bon ? (
                    <Badge variant={bon.usable ? 'success' : 'destructive'}>{bon.usable ? 'Bon valable' : bon.problem}</Badge>
                  ) : null}
                </div>
              </Champ>
            </div>
          )}
          {methode === 'OWN_TRUCK' && (
            <div className="sm:col-span-2">
              <Champ id="vente-camion" label="Camion de la société">
                <SearchableSelect
                  id="vente-camion"
                  value={form.truck_id || ''}
                  onValueChange={changer('truck_id')}
                  options={camions.map((camion) => ({ value: camion.id, label: camion.matricule || 'Camion', description: camion.tractor_plate || undefined }))}
                  aria-label="Camion de la société"
                  placeholder="Choisir le camion"
                  searchPlaceholder="Rechercher un camion…"
                />
              </Champ>
            </div>
          )}
          {!AVEC_CLIENT.includes(methode) && methode !== 'OWN_TRUCK' && (
            <Champ id="vente-nom" label="Nom du client">
              <Input id="vente-nom" value={form.customer_name || ''} onChange={changer('customer_name')} />
            </Champ>
          )}
          <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
            {prix ? <Badge variant="muted">Prix du litre {formatMontant(prix)}</Badge> : null}
            {estime !== null ? <Badge variant="muted">Montant {formatMontant(estime)}</Badge> : null}
          </div>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || !form.nozzle_id || (litres === null && montant === null)}>
              Enregistrer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const DepenseDialog = ({ open, onOpenChange, stationId, onSaved }) => {
  const [form, setForm] = useState({ category: '', amount: '', description: '', paid_in_cash: true });
  const [enCours, setEnCours] = useState(false);
  const cle = useRef(nouvelleCle());
  useEffect(() => {
    if (open) {
      setForm({ category: '', amount: '', description: '', paid_in_cash: true });
      cle.current = nouvelleCle();
    }
  }, [open]);
  const enregistrer = async (event) => {
    event.preventDefault();
    setEnCours(true);
    try {
      await stationsApi.expenses.record(stationId, {
        id: cle.current,
        category: form.category.trim(),
        amount: lireNombre(form.amount),
        description: form.description.trim() || undefined,
        paid_in_cash: form.paid_in_cash,
      });
      toast.success('Dépense enregistrée');
      onSaved();
      onOpenChange(false);
    } catch (probleme) {
      toast.error(messageErreur(probleme, 'Impossible d’enregistrer la dépense'));
    } finally {
      setEnCours(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Dépense</DialogTitle>
        </DialogHeader>
        <form onSubmit={enregistrer} className="grid gap-4">
          <Champ id="depense-motif" label="Motif">
            <Input id="depense-motif" value={form.category} onChange={(event) => setForm((avant) => ({ ...avant, category: event.target.value }))} required maxLength={100} />
          </Champ>
          <Champ id="depense-montant" label="Montant (FCFA)">
            <Input
              id="depense-montant"
              inputMode="numeric"
              value={form.amount}
              onChange={(event) => setForm((avant) => ({ ...avant, amount: event.target.value }))}
              required
              className="text-right tabular-nums"
            />
          </Champ>
          <Champ id="depense-detail" label="Détail">
            <Input id="depense-detail" value={form.description} onChange={(event) => setForm((avant) => ({ ...avant, description: event.target.value }))} maxLength={500} />
          </Champ>
          <label className="flex items-center gap-2 text-sm" htmlFor="depense-especes">
            <Checkbox
              id="depense-especes"
              checked={form.paid_in_cash}
              onCheckedChange={(valeur) => setForm((avant) => ({ ...avant, paid_in_cash: valeur === true }))}
            />
            Payée avec l’argent de la caisse
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || !form.category.trim() || !lireNombre(form.amount)}>
              Enregistrer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ReleveDialog = ({ open, onOpenChange, stationId, contexte, onSaved }) => {
  const [index, setIndex] = useState({});
  const [enCours, setEnCours] = useState(false);
  const cle = useRef(nouvelleCle());
  const attendus = useMemo(() => {
    const vus = new Set();
    return (contexte.shift?.pending_price_readings || []).filter((item) => {
      if (vus.has(item.nozzle_id)) return false;
      vus.add(item.nozzle_id);
      return true;
    });
  }, [contexte]);
  useEffect(() => {
    if (open) {
      setIndex({});
      cle.current = nouvelleCle();
    }
  }, [open]);
  const dernier = (nozzleId) => {
    const releves = (contexte.shift?.readings || []).filter((r) => r.nozzle_id === nozzleId && !r.voided_at);
    return releves[releves.length - 1]?.index;
  };
  const enregistrer = async (event) => {
    event.preventDefault();
    setEnCours(true);
    try {
      await stationsApi.shifts.priceReadings(
        stationId,
        contexte.shift.id,
        attendus
          .filter((item) => lireNombre(index[item.nozzle_id]) !== null)
          .map((item) => ({ nozzle_id: item.nozzle_id, index: lireNombre(index[item.nozzle_id]) })),
        cle.current,
      );
      toast.success('Index relevés');
      onSaved();
      onOpenChange(false);
    } catch (probleme) {
      toast.error(messageErreur(probleme, 'Impossible d’enregistrer les index'));
    } finally {
      setEnCours(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Relevé au changement de prix</DialogTitle>
        </DialogHeader>
        <form onSubmit={enregistrer} className="grid gap-3">
          <table className={CELLULES}>
            <thead>
              <tr>
                <th className="text-left">Pistolet</th>
                <th className="text-right">Dernier index</th>
                <th className="text-right">Index maintenant</th>
              </tr>
            </thead>
            <tbody>
              {attendus.map((item) => (
                <tr key={item.nozzle_id}>
                  <td className="whitespace-nowrap">
                    Pistolet {item.nozzle_number}
                    <span className="block text-xs text-muted-foreground">
                      {item.product_name} · {formatMontant(item.unit_price)} depuis {formatHeure(item.effective_at)}
                    </span>
                  </td>
                  <td className="text-right">
                    <Readout value={dernier(item.nozzle_id)} size="sm" />
                  </td>
                  <td className="text-right">
                    <Input
                      aria-label={`Index du pistolet ${item.nozzle_number}`}
                      inputMode="decimal"
                      value={index[item.nozzle_id] || ''}
                      onChange={(event) => setIndex((avant) => ({ ...avant, [item.nozzle_id]: event.target.value }))}
                      className="h-8 w-36 text-right tabular-nums"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || !attendus.some((item) => lireNombre(index[item.nozzle_id]) !== null)}>
              Enregistrer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const MotifDialog = ({ open, onOpenChange, titre, bouton, onConfirm }) => {
  const [motif, setMotif] = useState('');
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    if (open) setMotif('');
  }, [open]);
  const valider = async (event) => {
    event.preventDefault();
    setEnCours(true);
    try {
      await onConfirm(motif.trim());
      onOpenChange(false);
    } catch (probleme) {
      toast.error(messageErreur(probleme, 'Opération refusée'));
    } finally {
      setEnCours(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{titre}</DialogTitle>
        </DialogHeader>
        <form onSubmit={valider} className="grid gap-3">
          <Champ id="motif" label="Motif">
            <Textarea id="motif" rows={3} value={motif} onChange={(event) => setMotif(event.target.value)} required maxLength={1000} />
          </Champ>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || !motif.trim()}>
              {bouton}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PendantEtape = ({ contexte, stationId, droits, montantsVisibles, onChanged, onCloture }) => {
  const [dialogue, setDialogue] = useState(null);
  const poste = contexte.shift;
  const numeros = Object.fromEntries((contexte.nozzles || []).map((pistolet) => [pistolet.id, pistolet.number]));
  const peutVendre = droits.peutEcrire('station_sales') || droits.peutEcrire('station_pos');
  const peutDepenser = droits.peutEcrire('station_expenses');
  const peutPoste = droits.peutEcrire('shifts');
  const enAttente = poste.pending_price_readings || [];
  const parMoyen = Object.entries(poste.non_cash_by_method || {});

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
      <div className="grid content-start gap-4">
        <Panneau
          titre="Ventes hors espèces"
          droite={
            peutVendre && (
              <Button type="button" size="sm" onClick={() => setDialogue({ type: 'vente' })}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Vente
              </Button>
            )
          }
        >
          {(poste.sales || []).length === 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">Aucune vente hors espèces</p>
          ) : (
            <table className={CELLULES}>
              <thead>
                <tr>
                  <th className="text-left">Heure</th>
                  <th className="text-left">Pistolet</th>
                  <th className="text-left">Paiement</th>
                  <th className="text-left">Client</th>
                  <th className="text-right">Litres</th>
                  <th className="text-right">Montant</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {poste.sales.map((vente) => {
                  const annulee = vente.status === 'CANCELLED';
                  return (
                    <tr key={vente.id} className={annulee ? 'text-muted-foreground line-through' : undefined}>
                      <td className="whitespace-nowrap">{formatHeure(vente.sale_date)}</td>
                      <td>{numeros[vente.nozzle_id] ? `Pistolet ${numeros[vente.nozzle_id]}` : '—'}</td>
                      <td>{libelle('station_paiement', vente.payment_method)}</td>
                      <td>{vente.customer_name || vente.vehicle_plate || (vente.voucher_id ? 'Bon carburant' : null) || '—'}</td>
                      <td className="text-right tabular-nums">{formatNombre(vente.quantity_liters, 2)}</td>
                      <td className="text-right tabular-nums">{masque(montantsVisibles, vente.total_amount, (v) => formatNombre(v, 0))}</td>
                      <td className="text-right">
                        {annulee ? (
                          <Badge variant="muted">{libelle('station_vente', 'CANCELLED')}</Badge>
                        ) : (
                          peutVendre && (
                            <Button type="button" variant="ghost" size="sm" onClick={() => setDialogue({ type: 'annuler', vente })}>
                              <Ban className="h-4 w-4" aria-hidden="true" />
                              Annuler
                            </Button>
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Panneau>

        <Panneau
          titre="Dépenses"
          droite={
            peutDepenser && (
              <Button type="button" size="sm" variant="outline" onClick={() => setDialogue({ type: 'depense' })}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Dépense
              </Button>
            )
          }
        >
          {(poste.expenses || []).length === 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">Aucune dépense</p>
          ) : (
            <table className={CELLULES}>
              <thead>
                <tr>
                  <th className="text-left">Heure</th>
                  <th className="text-left">Motif</th>
                  <th className="text-left">Caisse</th>
                  <th className="text-right">Montant</th>
                </tr>
              </thead>
              <tbody>
                {poste.expenses.map((depense) => (
                  <tr key={depense.id}>
                    <td className="whitespace-nowrap">{formatHeure(depense.expense_date)}</td>
                    <td>
                      {depense.category}
                      {depense.description ? <span className="block text-xs text-muted-foreground">{depense.description}</span> : null}
                    </td>
                    <td>{depense.paid_in_cash ? 'Espèces' : 'Hors caisse'}</td>
                    <td className="text-right tabular-nums">{masque(montantsVisibles, depense.amount, (v) => formatNombre(v, 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panneau>
      </div>

      <div className="grid content-start gap-4">
        <Panneau titre={nomDuPoste(poste.label)} droite={<Badge variant="warning">Ouvert depuis {formatHeure(poste.opened_at)}</Badge>}>
          <table className={CELLULES}>
            <tbody>
              <tr>
                <td>Pompistes</td>
                <td className="text-right">
                  {[...new Set((poste.assignments || []).map((item) => item.attendant_name).filter(Boolean))].join(', ') || '—'}
                </td>
              </tr>
              <tr>
                <td>Fond de caisse</td>
                <td className="text-right tabular-nums">{masque(montantsVisibles, poste.opening_cash, (v) => formatNombre(v, 0))}</td>
              </tr>
              {parMoyen.map(([code, montant]) => (
                <tr key={code}>
                  <td>{libelle('station_paiement', code)}</td>
                  <td className="text-right tabular-nums">{masque(montantsVisibles, montant, (v) => formatNombre(v, 0))}</td>
                </tr>
              ))}
              <tr>
                <td>Dépenses payées en espèces</td>
                <td className="text-right tabular-nums">{masque(montantsVisibles, poste.cash_expenses, (v) => formatNombre(v, 0))}</td>
              </tr>
            </tbody>
          </table>
        </Panneau>

        {enAttente.length > 0 && (
          <Panneau titre="Changement de prix" droite={<Badge variant="destructive">Relevé à faire</Badge>}>
            <ul className="grid gap-1 px-3 text-sm">
              {enAttente.map((item) => (
                <li key={`${item.nozzle_id}-${item.effective_at}`}>
                  Pistolet {item.nozzle_number} · {item.product_name} à {formatMontant(item.unit_price)} depuis{' '}
                  {formatHeure(item.effective_at)}
                </li>
              ))}
            </ul>
            {peutPoste && (
              <div className="px-3 pt-2">
                <Button type="button" size="sm" onClick={() => setDialogue({ type: 'releve' })}>
                  Relever les index
                </Button>
              </div>
            )}
          </Panneau>
        )}

        {peutPoste && (
          <div className="flex justify-end">
            <Button type="button" onClick={onCloture}>
              Passer à la clôture
            </Button>
          </div>
        )}
      </div>

      <VenteDialog
        open={dialogue?.type === 'vente'}
        onOpenChange={(ouvert) => !ouvert && setDialogue(null)}
        stationId={stationId}
        contexte={contexte}
        onSaved={onChanged}
      />
      <DepenseDialog
        open={dialogue?.type === 'depense'}
        onOpenChange={(ouvert) => !ouvert && setDialogue(null)}
        stationId={stationId}
        onSaved={onChanged}
      />
      <ReleveDialog
        open={dialogue?.type === 'releve'}
        onOpenChange={(ouvert) => !ouvert && setDialogue(null)}
        stationId={stationId}
        contexte={contexte}
        onSaved={onChanged}
      />
      <MotifDialog
        open={dialogue?.type === 'annuler'}
        onOpenChange={(ouvert) => !ouvert && setDialogue(null)}
        titre="Annuler la vente"
        bouton="Annuler la vente"
        onConfirm={async (motif) => {
          await stationsApi.sales.cancel(stationId, dialogue.vente.id, motif);
          toast.success('Vente annulée');
          onChanged();
        }}
      />
    </div>
  );
};

// --- 3. Clôture -----------------------------------------------------------------------------

const ClotureEtape = ({ contexte, stationId, montantsVisibles, onRetour, onClosed }) => {
  const poste = contexte.shift;
  const brouillon = useMemo(() => lireBrouillon(poste.id) || {}, [poste.id]);
  const [saisies, setSaisies] = useState(brouillon.saisies || {});
  const [especes, setEspeces] = useState(brouillon.especes || '');
  const [declares, setDeclares] = useState(brouillon.declares || {});
  const [parPompiste, setParPompiste] = useState(brouillon.parPompiste || {});
  const [justification, setJustification] = useState(brouillon.justification || '');
  const [details, setDetails] = useState({});
  const [releves, setReleves] = useState(Boolean(Object.keys(brouillon.declares || {}).length));
  const [serveur, setServeur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const cle = useRef(nouvelleCle());
  const seuil = Number(contexte.station?.settings?.cash_threshold ?? 5000);

  const local = useMemo(
    () =>
      apercuLocal({
        shift: poste,
        nozzles: contexte.nozzles,
        prices: contexte.prices,
        saisies,
        especes,
        especesParPompiste: parPompiste,
      }),
    [poste, contexte.nozzles, contexte.prices, saisies, especes, parPompiste],
  );
  const corps = useMemo(
    () => corpsCloture({ saisies, especes, declares: releves ? declares : {}, especesParPompiste: parPompiste, justification }),
    [saisies, especes, declares, releves, parPompiste, justification],
  );
  const corpsApercu = JSON.stringify([corps.closingReadings, corps.declaredPayments, corps.declaredCashByAttendant]);

  useEffect(() => {
    if (!corps.closingReadings.length) {
      setServeur(null);
      return undefined;
    }
    let actif = true;
    const minuterie = setTimeout(() => {
      stationsApi.shifts
        .preview(stationId, poste.id, corps)
        .then((resultat) => actif && setServeur(resultat))
        .catch(() => actif && setServeur(null));
    }, 600);
    return () => {
      actif = false;
      clearTimeout(minuterie);
    };
    // `corpsApercu` résume `corps` : l'aperçu ne repart que si la saisie change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId, poste.id, corpsApercu]);

  const serveurParPistolet = Object.fromEntries((serveur?.nozzles || []).map((ligne) => [ligne.nozzle_id, ligne]));
  const ventes = serveur?.sales_amount ?? local.sales_amount;
  const attendues = serveur?.expected_cash ?? local.expected_cash;
  const ecart = serveur?.cash_variance ?? local.cash_variance;
  const pompistes = serveur?.attendants || local.attendants;
  const manquants = serveur?.missing_price_readings || [];
  const aJustifier = montantsVisibles ? depasseSeuil(ecart, seuil) : Boolean(serveur?.justification_required);
  const pompistesAffectes = [...new Map((poste.assignments || []).map((item) => [item.attendant_id, item])).values()];
  const moyens = [...new Set([...Object.keys(poste.non_cash_by_method || {}), ...NON_ESPECES.filter((code) => declares[code])])];
  const tousLesIndex = local.lignes.length > 0 && local.lignes.every((ligne) => ligne.end_index !== null && !ligne.error);

  const changerSaisie = (nozzleId, champ, valeur) =>
    setSaisies((avant) => ({ ...avant, [nozzleId]: { ...(avant[nozzleId] || {}), [champ]: valeur } }));

  const enregistrerBrouillon = () => {
    const ok = ecrireBrouillon(poste.id, { saisies, especes, declares, parPompiste, justification });
    if (ok) toast.success('Saisie enregistrée');
    else toast.error('Impossible d’enregistrer la saisie');
  };

  const cloturer = async () => {
    setEnCours(true);
    try {
      const resultat = await stationsApi.shifts.close(stationId, poste.id, { ...corps, idempotencyKey: cle.current });
      ecrireBrouillon(poste.id, null);
      toast.success(`${nomDuPoste(poste.label)} clôturé`);
      onClosed(resultat);
    } catch (probleme) {
      toast.error(messageErreur(probleme, 'Impossible de clôturer le poste'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="grid gap-4">
      <Panneau titre="Index des pistolets" droite={<span className="text-xs text-muted-foreground">Index de début repris de l’ouverture</span>}>
        <table className={CELLULES}>
          <thead>
            <tr>
              <th className="text-left">Pistolet</th>
              <th className="text-left">Produit</th>
              <th className="text-left">Pompiste</th>
              <th className="text-right">Index début</th>
              <th className="text-right">Index fin</th>
              <th className="text-right">Litres</th>
              <th className="text-right">Prix</th>
              <th className="text-right">Montant</th>
              <th aria-label="Détails" />
            </tr>
          </thead>
          <tbody>
            {local.lignes.map((ligne) => {
              const saisie = saisies[ligne.nozzle_id] || {};
              const duServeur = serveurParPistolet[ligne.nozzle_id];
              const prixServeur = duServeur?.unit_prices?.length === 1 ? duServeur.unit_prices[0] : null;
              const montant = duServeur ? duServeur.amount : ligne.amount;
              return (
                <React.Fragment key={ligne.nozzle_id}>
                  <tr>
                    <td className="whitespace-nowrap font-medium">Pistolet {ligne.number}</td>
                    <td>{ligne.product_name}</td>
                    <td>{ligne.attendant_name || '—'}</td>
                    <td className="text-right">
                      <Readout value={ligne.start_index} size="sm" />
                    </td>
                    <td className="text-right">
                      <Input
                        aria-label={`Index de fin du pistolet ${ligne.number}`}
                        inputMode="decimal"
                        value={saisie.index || ''}
                        onChange={(event) => changerSaisie(ligne.nozzle_id, 'index', event.target.value)}
                        aria-invalid={Boolean(ligne.error)}
                        className="ml-auto h-8 w-36 text-right font-mono tabular-nums"
                      />
                      <PhotoIndex
                        stationId={stationId}
                        nozzleId={ligne.nozzle_id}
                        kind="CLOSING"
                        onUtiliser={({ index: lu, documentId }) =>
                          setSaisies((avant) => ({
                            ...avant,
                            [ligne.nozzle_id]: {
                              ...(avant[ligne.nozzle_id] || {}),
                              index: String(lu),
                              photoIndex: lu,
                              photoDocumentId: documentId || undefined,
                            },
                          }))
                        }
                      />
                    </td>
                    <td className="text-right tabular-nums">{ligne.liters === null ? '—' : formatNombre(ligne.liters, 1)}</td>
                    <td className="text-right tabular-nums">
                      {prixServeur ?? ligne.unit_price ? formatNombre(prixServeur ?? ligne.unit_price, 0) : duServeur ? 'plusieurs' : '—'}
                    </td>
                    <td className="text-right tabular-nums">{masque(montantsVisibles, montant, (v) => (v === null || v === undefined ? '—' : formatNombre(v, 0)))}</td>
                    <td className="text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-expanded={Boolean(details[ligne.nozzle_id])}
                        onClick={() => setDetails((avant) => ({ ...avant, [ligne.nozzle_id]: !avant[ligne.nozzle_id] }))}
                      >
                        Essais
                      </Button>
                    </td>
                  </tr>
                  {ligne.error && (
                    <tr>
                      <td colSpan={9} className="text-sm font-medium text-destructive" role="alert">
                        {ligne.error}
                      </td>
                    </tr>
                  )}
                  {details[ligne.nozzle_id] && (
                    <tr>
                      <td colSpan={9}>
                        <div className="flex flex-wrap items-end gap-3">
                          <Champ id={`essais-${ligne.nozzle_id}`} label="Essais de pompe (L)">
                            <Input
                              id={`essais-${ligne.nozzle_id}`}
                              inputMode="decimal"
                              value={saisie.tests || ''}
                              onChange={(event) => changerSaisie(ligne.nozzle_id, 'tests', event.target.value)}
                              className="h-8 w-28 text-right tabular-nums"
                            />
                          </Champ>
                          <label className="flex items-center gap-2 pb-1 text-sm" htmlFor={`raz-${ligne.nozzle_id}`}>
                            <Checkbox
                              id={`raz-${ligne.nozzle_id}`}
                              checked={Boolean(saisie.meterReset)}
                              onCheckedChange={(valeur) => changerSaisie(ligne.nozzle_id, 'meterReset', valeur === true)}
                            />
                            Compteur remis à zéro
                          </label>
                          {saisie.meterReset && (
                            <Champ id={`motif-${ligne.nozzle_id}`} label="Motif">
                              <Input
                                id={`motif-${ligne.nozzle_id}`}
                                value={saisie.motif || ''}
                                onChange={(event) => changerSaisie(ligne.nozzle_id, 'motif', event.target.value)}
                                className="h-8 w-64"
                              />
                            </Champ>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5}>Ventes du poste</td>
              <td className="text-right tabular-nums">
                {(serveur?.sales_liters ?? local.sales_liters) === null ? '—' : formatNombre(serveur?.sales_liters ?? local.sales_liters, 1)}
              </td>
              <td />
              <td className="text-right tabular-nums">{masque(montantsVisibles, ventes, (v) => (v === null || v === undefined ? '—' : formatNombre(v, 0)))}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </Panneau>

      {manquants.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm" role="alert">
          <Badge variant="destructive">Relevé au changement de prix manquant</Badge>
          {[...new Set(manquants.map((item) => `Pistolet ${item.nozzle_number}`))].join(', ')}
          <Button type="button" size="sm" variant="outline" onClick={onRetour}>
            Relever les index
          </Button>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Panneau
          titre="Caisse"
          droite={
            <Button type="button" variant="ghost" size="sm" aria-pressed={releves} onClick={() => setReleves((avant) => !avant)}>
              Relevés par moyen de paiement
            </Button>
          }
        >
          <table className={CELLULES}>
            <tbody>
              <tr>
                <td>Fond de caisse</td>
                <td className="text-right tabular-nums">{masque(montantsVisibles, poste.opening_cash, (v) => formatNombre(v, 0))}</td>
              </tr>
              <tr>
                <td>Ventes du poste</td>
                <td className="text-right tabular-nums">{masque(montantsVisibles, ventes, (v) => (v === null || v === undefined ? '—' : formatNombre(v, 0)))}</td>
              </tr>
              {moyens.map((code) => (
                <tr key={code}>
                  <td>{libelle('station_paiement', code)}</td>
                  <td className="text-right tabular-nums">
                    {masque(montantsVisibles, (poste.non_cash_by_method || {})[code], (v) => `−${formatNombre(v || 0, 0)}`)}
                    {releves && (
                      <Input
                        aria-label={`Relevé ${libelle('station_paiement', code)}`}
                        inputMode="numeric"
                        placeholder="Relevé"
                        value={declares[code] || ''}
                        onChange={(event) => setDeclares((avant) => ({ ...avant, [code]: event.target.value }))}
                        className="ml-auto mt-1 h-8 w-36 text-right tabular-nums"
                      />
                    )}
                  </td>
                </tr>
              ))}
              <tr>
                <td>Dépenses payées en espèces</td>
                <td className="text-right tabular-nums">{masque(montantsVisibles, poste.cash_expenses, (v) => `−${formatNombre(v || 0, 0)}`)}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td>Espèces attendues</td>
                <td className="text-right tabular-nums">{masque(montantsVisibles, attendues, (v) => (v === null || v === undefined ? '—' : formatNombre(v, 0)))}</td>
              </tr>
              <tr>
                <td>
                  <label htmlFor="especes-comptees">Espèces comptées</label>
                </td>
                <td className="text-right">
                  <Input
                    id="especes-comptees"
                    inputMode="numeric"
                    value={especes}
                    onChange={(event) => setEspeces(event.target.value)}
                    className="ml-auto h-9 w-40 text-right font-mono text-base tabular-nums"
                  />
                </td>
              </tr>
              {pompistesAffectes.map((item) => (
                <tr key={item.attendant_id}>
                  <td>
                    <label htmlFor={`especes-${item.attendant_id}`}>Remis par {item.attendant_name}</label>
                  </td>
                  <td className="text-right">
                    <Input
                      id={`especes-${item.attendant_id}`}
                      inputMode="numeric"
                      value={parPompiste[item.attendant_id] || ''}
                      onChange={(event) => setParPompiste((avant) => ({ ...avant, [item.attendant_id]: event.target.value }))}
                      className="ml-auto h-8 w-40 text-right tabular-nums"
                    />
                  </td>
                </tr>
              ))}
            </tfoot>
          </table>
        </Panneau>

        <div className="grid content-start gap-4">
          <VarianceVerdict variance={ecart} threshold={seuil} attendants={pompistes} hidden={!montantsVisibles} />
          {aJustifier && (
            <Panneau titre="Justification" droite={<Badge variant="destructive">Seuil {formatMontant(seuil)}</Badge>}>
              <div className="px-3">
                <label htmlFor="justification" className="mb-1 block text-sm font-medium">
                  Ce que vous savez de cet écart
                </label>
                <Textarea id="justification" rows={3} value={justification} onChange={(event) => setJustification(event.target.value)} maxLength={1000} />
              </div>
            </Panneau>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" onClick={onRetour}>
              Retour
            </Button>
            <Button type="button" variant="outline" onClick={enregistrerBrouillon}>
              Enregistrer sans clôturer
            </Button>
            <Button
              type="button"
              onClick={cloturer}
              disabled={enCours || !tousLesIndex || lireNombre(especes) === null || manquants.length > 0 || (aJustifier && !justification.trim())}
            >
              Clôturer le poste
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- 4. Arrêt de la journée -------------------------------------------------------------------

const ArretEtape = ({ stationId, date, droits, montantsVisibles, onChanged }) => {
  const [journee, setJournee] = useState(null);
  const [justification, setJustification] = useState('');
  const [dialogue, setDialogue] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const charger = useCallback(async () => {
    if (!stationId || !date) return;
    try {
      setJournee(await stationsApi.days.get(stationId, date));
    } catch (probleme) {
      toast.error(messageErreur(probleme, 'Impossible de charger la journée'));
    }
  }, [stationId, date]);
  useEffect(() => {
    charger();
  }, [charger]);

  if (!journee) return <LoadingState shape="table" />;
  const ouverte = journee.status === 'OPEN';
  const posteOuvert = (journee.shifts || []).find((poste) => poste.status === 'OPEN');
  const seuil = Number(journee.settings?.cash_threshold ?? 5000);

  const arreter = async () => {
    setEnCours(true);
    try {
      setJournee(await stationsApi.days.lock(stationId, date, justification.trim() || undefined));
      toast.success('Journée arrêtée');
      onChanged();
    } catch (probleme) {
      toast.error(messageErreur(probleme, 'Impossible d’arrêter la journée'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="grid gap-4">
      <Panneau
        titre={`Journée du ${formatDate(date)}`}
        droite={<Badge variant={ouverte ? 'warning' : 'success'}>{libelle('station_journee', journee.status)}</Badge>}
      >
        {(journee.shifts || []).length === 0 ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">Aucun poste ce jour</p>
        ) : (
          <table className={CELLULES}>
            <thead>
              <tr>
                <th className="text-left">Poste</th>
                <th className="text-left">Heures</th>
                <th className="text-left">Pompistes</th>
                <th className="text-right">Litres</th>
                <th className="text-right">Ventes</th>
                <th className="text-right">Écart de caisse</th>
                <th className="text-left">État</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {journee.shifts.map((poste) => (
                <tr key={poste.id}>
                  <td className="whitespace-nowrap font-medium">{nomDuPoste(poste.label)}</td>
                  <td className="whitespace-nowrap">
                    {formatHeure(poste.opened_at)}
                    {poste.closed_at ? ` à ${formatHeure(poste.closed_at)}` : ''}
                  </td>
                  <td>{(poste.attendant_names || []).join(', ') || '—'}</td>
                  <td className="text-right tabular-nums">{formatNombre(poste.total_sales_liters, 1)}</td>
                  <td className="text-right tabular-nums">{masque(montantsVisibles, poste.total_sales_amount, (v) => formatNombre(v, 0))}</td>
                  <td className={`text-right tabular-nums ${poste.cash_exceeds ? 'font-semibold text-destructive' : ''}`}>
                    {masque(montantsVisibles, poste.cash_variance, (v) => (v === null || v === undefined ? '—' : signe(v)))}
                  </td>
                  <td>{libelle('station_poste', poste.status)}</td>
                  <td className="text-right">
                    {ouverte && poste.status === 'CLOSED' && droits.peutEcrire('shifts') && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => setDialogue({ type: 'rouvrirPoste', poste })}>
                        Rouvrir
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3}>Total</td>
                <td className="text-right tabular-nums">{formatNombre(journee.totals?.liters, 1)}</td>
                <td className="text-right tabular-nums">{masque(montantsVisibles, journee.totals?.sales_amount, (v) => formatNombre(v, 0))}</td>
                <td className="text-right tabular-nums">{masque(montantsVisibles, journee.totals?.cash_variance, signe)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        )}
      </Panneau>

      {ouverte ? (
        <div className="grid gap-3">
          {posteOuvert && (
            <div>
              <Badge variant="warning">{nomDuPoste(posteOuvert.label)} ouvert : clôturez-le d’abord</Badge>
            </div>
          )}
          {(journee.tank_checks || []).some((c) => c.closing_dip_missing || c.exceeds) && (
            <div className="flex flex-wrap items-center gap-2">
              {(journee.tank_checks || []).filter((c) => c.closing_dip_missing).map((c) => (
                <Badge key={`jauge-${c.tank_id}`} variant="warning">{c.tank_name || 'Cuve'} : fin de journée à jauger</Badge>
              ))}
              {(journee.tank_checks || []).filter((c) => c.exceeds).map((c) => (
                <Badge key={`ecart-${c.tank_id}`} variant="destructive">
                  {c.tank_name || 'Cuve'} : écart {signe(c.variance_liters)} L
                </Badge>
              ))}
              {(journee.tank_checks || []).some((c) => c.closing_dip_missing) && (
                <Button asChild variant="outline" size="sm">
                  <Link to="/stations/cuves">Jauger</Link>
                </Button>
              )}
            </div>
          )}
          {journee.justification_required && (
            <Panneau titre="Justification" droite={<Badge variant="destructive">Seuil {formatMontant(seuil)}</Badge>}>
              <div className="px-3">
                <label htmlFor="justification-journee" className="mb-1 block text-sm font-medium">
                  Ce que vous savez des écarts du jour
                </label>
                <Textarea id="justification-journee" rows={3} value={justification} onChange={(event) => setJustification(event.target.value)} maxLength={1000} />
              </div>
            </Panneau>
          )}
          {droits.peutEcrire('shifts') && (
            <div className="flex justify-end">
              <Button
                type="button"
                onClick={arreter}
                disabled={enCours || !journee.can_lock || (journee.justification_required && !justification.trim())}
              >
                Arrêter la journée
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {journee.lock_justification && <span className="mr-auto text-sm">Justification : {journee.lock_justification}</span>}
          <Button asChild variant="outline">
            <Link to={`/stations/rapports?station=${stationId}&date=${date}`}>
              <FileText className="h-4 w-4" aria-hidden="true" />
              Rapport du jour
            </Link>
          </Button>
          {droits.estAdministrateur && (
            <Button type="button" variant="outline" onClick={() => setDialogue({ type: 'rouvrirJournee' })}>
              Rouvrir la journée
            </Button>
          )}
          {droits.estAdministrateur && journee.head_office_validation && journee.status === 'LOCKED' && (
            <Button
              type="button"
              onClick={async () => {
                try {
                  setJournee(await stationsApi.days.validate(stationId, date));
                  toast.success('Journée validée');
                } catch (probleme) {
                  toast.error(messageErreur(probleme, 'Validation refusée'));
                }
              }}
            >
              Valider pour le siège
            </Button>
          )}
        </div>
      )}

      <MotifDialog
        open={dialogue?.type === 'rouvrirJournee'}
        onOpenChange={(ouvert) => !ouvert && setDialogue(null)}
        titre="Rouvrir la journée"
        bouton="Rouvrir"
        onConfirm={async (motif) => {
          setJournee(await stationsApi.days.unlock(stationId, date, motif));
          toast.success('Journée rouverte');
          onChanged();
        }}
      />
      <MotifDialog
        open={dialogue?.type === 'rouvrirPoste'}
        onOpenChange={(ouvert) => !ouvert && setDialogue(null)}
        titre="Rouvrir le poste"
        bouton="Rouvrir le poste"
        onConfirm={async (motif) => {
          await stationsApi.shifts.reopen(stationId, dialogue.poste.id, motif);
          toast.success('Poste rouvert');
          onChanged({ reopened: true });
        }}
      />
    </div>
  );
};

// --- Page -----------------------------------------------------------------------------------

const ShiftPage = () => {
  const { stationId: parametre } = useParams();
  const navigate = useNavigate();
  const { stations, loading: chargementStations } = useStations();
  const droits = useDroitsStation();
  const { canSeeMoney } = useAuth();
  const [stationId, setStationId] = useState(parametre || '');
  const [contexte, setContexte] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [etape, setEtape] = useState(null);
  const [dateArret, setDateArret] = useState(null);

  useEffect(() => {
    if (parametre) setStationId(parametre);
  }, [parametre]);

  const choisirStation = useCallback(
    (id) => {
      setStationId(id);
      if (id && id !== parametre) navigate(`/stations/${id}/poste`, { replace: !parametre });
    },
    [navigate, parametre],
  );

  const charger = useCallback(async (id) => {
    if (!id) return;
    setChargement(true);
    try {
      setContexte(await stationsApi.shifts.current(id));
    } catch (probleme) {
      toast.error(messageErreur(probleme, 'Impossible de charger le poste'));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    setContexte(null);
    setEtape(null);
    setDateArret(null);
    charger(stationId);
  }, [stationId, charger]);

  const poste = contexte?.shift || null;
  const montantsVisibles = Boolean(canSeeMoney) && !contexte?.amounts_hidden;
  const etapeCourante = etape ?? (poste ? 2 : 1);
  const station = contexte?.station;
  const sansPrix = (contexte?.prices || []).filter((prix) => prix.unit_price === null || prix.unit_price === undefined);
  const relevesEnAttente = poste?.pending_price_readings?.length || 0;
  const dateJournee = dateArret || contexte?.last_closed_shift?.business_date || contexte?.business_date;

  const titre = !poste
    ? 'Poste du jour'
    : etapeCourante === 3
      ? `Clôture du ${nomDuPoste(poste.label).replace(/^Poste /, 'poste ')}`
      : nomDuPoste(poste.label);

  if (!chargementStations && stations.length === 0) {
    return (
      <div className="mx-auto w-full max-w-page px-4 py-6">
        <EmptyState
          variant="empty"
          title="Aucune station"
          description="Créez la station, ses cuves et ses pistolets dans les réglages."
          actionLabel="Réglages des stations"
          onAction={() => navigate('/stations/reglages')}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-page space-y-4 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Stations"
        title={titre}
        description="Ouvrir le poste, saisir les ventes hors espèces et les dépenses, clôturer avec les index et la caisse, arrêter la journée."
        meta={
          station
            ? `${nomLisible(station.name, station.code)}${poste ? `, ${formatDate(poste.opened_at)} depuis ${formatHeure(poste.opened_at)}` : ''}`
            : undefined
        }
        alertes={[
          sansPrix.length > 0 && {
            niveau: 'critique',
            texte: `Prix manquant : ${sansPrix.map((prix) => prix.product_name).join(', ')}`,
            court: 'Prix manquant',
            nombre: sansPrix.length,
          },
          relevesEnAttente > 0 && {
            niveau: 'critique',
            texte: 'Le prix a changé pendant le poste : relevez les index des pistolets concernés',
            court: 'Relevé de prix à faire',
            nombre: relevesEnAttente,
          },
        ].filter(Boolean)}
        actions={
          <>
            <StationPicker stations={stations} value={stationId} onChange={choisirStation} />
            {!poste && contexte && droits.peutEcrire('shifts') && etapeCourante !== 4 && (
              <Button type="button" variant="outline" onClick={() => setEtape(4)}>
                Arrêter la journée
              </Button>
            )}
          </>
        }
      />

      <ShiftSteps
        current={etapeCourante}
        onSelect={poste ? (numero) => setEtape(numero === 1 ? 2 : numero) : undefined}
      />

      {!stationId || (chargement && !contexte) ? (
        <LoadingState shape="table" />
      ) : !contexte ? (
        <EmptyState variant="error" title="Poste indisponible" description="Le poste de cette station n’a pas pu être chargé." actionLabel="Réessayer" onAction={() => charger(stationId)} />
      ) : etapeCourante === 4 ? (
        <ArretEtape
          stationId={stationId}
          date={dateJournee}
          droits={droits}
          montantsVisibles={montantsVisibles}
          onChanged={(effet) => {
            if (effet?.reopened) setEtape(null);
            charger(stationId);
          }}
        />
      ) : !poste ? (
        <OuvertureEtape
          key={contexte.last_closed_shift?.id || 'premier'}
          contexte={contexte}
          stationId={stationId}
          peutEcrire={droits.peutEcrire('shifts')}
          onOpened={() => {
            setEtape(null);
            charger(stationId);
          }}
        />
      ) : etapeCourante === 3 ? (
        <ClotureEtape
          contexte={contexte}
          stationId={stationId}
          montantsVisibles={montantsVisibles}
          onRetour={() => setEtape(2)}
          onClosed={(resultat) => {
            setDateArret(resultat?.business_date || poste.business_date);
            setEtape(4);
            charger(stationId);
          }}
        />
      ) : (
        <PendantEtape
          contexte={contexte}
          stationId={stationId}
          droits={droits}
          montantsVisibles={montantsVisibles}
          onChanged={() => charger(stationId)}
          onCloture={() => setEtape(3)}
        />
      )}
    </div>
  );
};

export default ShiftPage;
